import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Pills } from '@saleshandy/design-system';
import { ChevronUp } from '@saleshandy/icons';

import Input from '../../../../../shared/design-system/components/input';
import Modal from '../../../../../shared/design-system/ui/modal';

import { Team as TeamType } from '../../../../settings/types/users-and-teams';
import { RequestStatus } from '../../../../../shared/enums/request-status';
import {
  getIsRequestPending,
  truncate,
  accessibleOnClick,
} from '../../../../../shared/utils';
import hasPermission from '../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../shared/utils/access-control/enums/permissions';
import ProspectFiltersSelectDropdown from '../../../../prospect/components/prospect-filters-modal/components/prospect-filters-select-dropdown';
import ProspectFiltersMultiSelectDropdown from '../../../../prospect/components/prospect-filters-modal/components/prospect-filters-multi-select-dropdown';
import { LeadFinderListVisibility } from '../../../types';

type IProps = {
  show: boolean;
  onHide: VoidFunction;
  showBackIcon?: boolean;
  onBackClick?: VoidFunction;
  onExited?: VoidFunction;
  onSave: (
    listId: number | null,
    listName: string,
    isShared: boolean,
    teamIds: number[],
  ) => void;
  isLoading: boolean;
  teamsList: TeamType[];
  listDetails?: {
    listId: number;
    name: string;
    visibility: LeadFinderListVisibility;
    teams?: any[];
  };
  getTeamsListRequestStatus: RequestStatus;
  sendGetTeamsListRequest: VoidFunction;
  isCreateMode?: boolean;
  existingLists?: Array<{ id?: number; label?: string }>;
  primaryButtonLabel?: string;
};

enum SharedWith {
  Public = 'public',
  Private = 'private',
  Team = 'team',
}

const CreateEditSavedListModal: React.FC<IProps> = ({
  show,
  onHide,
  showBackIcon = false,
  onBackClick,
  onExited,
  onSave,
  isLoading,
  listDetails,
  teamsList,
  getTeamsListRequestStatus,
  sendGetTeamsListRequest,
  isCreateMode = false,
  existingLists = [],
  primaryButtonLabel = 'Save',
}) => {
  const tIds = listDetails?.teams?.map((team) => team.id) || [];

  const canViewTeamOption =
    hasPermission(Permissions.ACCOUNT_TEAM_MEMBER_READ) ||
    hasPermission(Permissions.TEAM_MEMBER_READ) ||
    hasPermission(Permissions.TEAM_TEAM_MEMBER_READ);

  const getTeamsListRequestStatusLoading = getIsRequestPending(
    getTeamsListRequestStatus,
  );

  const initialSharedWith = () => {
    if (listDetails?.teams?.length) {
      if (
        teamsList.length === 0 &&
        canViewTeamOption &&
        !getTeamsListRequestStatusLoading
      ) {
        sendGetTeamsListRequest();
      }

      return SharedWith.Team;
    }
    if (listDetails?.visibility === LeadFinderListVisibility.Public) {
      return SharedWith.Public;
    }

    return SharedWith.Private;
  };

  const [filterName, setFilterName] = useState(listDetails?.name || '');
  const [filterNameError, setFilterNameError] = useState('');
  const [sharedWith, setSharedWith] = useState<SharedWith>(initialSharedWith());
  const [teamIds, setTeamIds] = useState<number[]>(tIds);
  const previousListIdRef = useRef<number | undefined>(listDetails?.listId);
  const previousShowRef = useRef<boolean>(show);

  useEffect(() => {
    // Only update when modal opens (show changes from false to true) or when editing a different list
    const isModalJustOpened = !previousShowRef.current && show;
    const isDifferentList = listDetails?.listId !== previousListIdRef.current;
    const wasCreateMode =
      previousListIdRef.current === undefined && !isCreateMode;

    if (!show) {
      previousShowRef.current = show;
      return;
    }

    if (isModalJustOpened || isDifferentList || wasCreateMode) {
      if (isCreateMode) {
        // Reset form for create mode
        setFilterName('');
        setFilterNameError('');
        setSharedWith(SharedWith.Private);
        setTeamIds([]);
      } else if (listDetails) {
        setFilterName(listDetails.name || '');
        setFilterNameError('');
        const initialShared = initialSharedWith();
        setSharedWith(initialShared);
        if (initialShared === SharedWith.Team) {
          setTeamIds(listDetails.teams?.map((team) => team.id) || []);
        } else {
          setTeamIds([]);
        }
      }
      previousListIdRef.current = listDetails?.listId;
    }

    previousShowRef.current = show;
  }, [listDetails?.listId, isCreateMode, show]);

  const shareWithOptions = useMemo(
    () =>
      [
        {
          key: SharedWith.Public,
          label: 'Public (Everyone)',
        },
        {
          key: SharedWith.Private,
          label: 'Private (Only me)',
        },
        canViewTeamOption && {
          key: SharedWith.Team,
          label: 'Team',
        },
      ].filter((option) => option),
    [canViewTeamOption],
  );

  const teamsOptions = useMemo(
    () =>
      teamsList?.map((team) => ({
        key: team.id.toString(),
        id: Number(team.id),
        name: team.name,
      })) || [],
    [teamsList],
  );

  const handleSave = () => {
    if (isCreateMode) {
      onSave(
        null,
        filterName.trim(),
        sharedWith === SharedWith.Public,
        sharedWith === SharedWith.Team ? teamIds.map((id) => Number(id)) : [],
      );
    } else if (listDetails) {
      onSave(
        listDetails.listId,
        filterName.trim(),
        sharedWith === SharedWith.Public,
        sharedWith === SharedWith.Team ? teamIds.map((id) => Number(id)) : [],
      );
    }
  };

  useEffect(() => {
    if (!show && isCreateMode) {
      setFilterName('');
      setFilterNameError('');
      setSharedWith(SharedWith.Private);
      setTeamIds([]);
    }
  }, [show, isCreateMode]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      title={
        isCreateMode ? (
          <div className="d-flex align-items-center gap-8">
            {showBackIcon && (
              <span
                {...accessibleOnClick(() => {
                  onBackClick?.();
                })}
                className="back-icon"
              >
                <ChevronUp width={16} height={16} color="#6B7280" />
              </span>
            )}
            <span>Create New List</span>
          </div>
        ) : (
          'Edit List'
        )
      }
      primaryBtnLabel={primaryButtonLabel}
      primaryBtnDisabled={
        filterName.trim() === '' ||
        filterNameError !== '' ||
        (sharedWith === SharedWith.Team && teamIds?.length === 0)
      }
      primaryBtnLoading={isLoading}
      secondaryBtnDisabled={isLoading}
      primaryBtnClickedHandler={handleSave}
      secondaryBtnClickedHandler={onHide}
      className="create-edit-saved-list-modal"
      extraModalProps={{
        onExited,
      }}
    >
      <div className="create-edit-saved-list-modal--input-group">
        <p>List Name</p>
        <Input
          placeholder="Type name"
          autoFocus
          value={filterName}
          onChange={(value) => {
            const newValue = value || '';
            setFilterName(newValue);
            // Length validation
            if (newValue.length > 128 && filterNameError === '') {
              setFilterNameError(
                'Name must not be greater than 128 characters.',
              );
              return;
            }

            // Trimmed value for duplicate check
            const trimmed = newValue.trim();

            // Clear previous non-duplicate errors if any
            if (newValue.length <= 128 && filterNameError !== '') {
              setFilterNameError('');
            }

            if (trimmed.length > 0) {
              const duplicate = existingLists?.some((list) => {
                const listLabel = list?.label || '';
                // If editing, allow same name for the same list id
                if (
                  listDetails &&
                  listDetails.listId &&
                  list.id === listDetails.listId
                ) {
                  return false;
                }
                return listLabel.trim().toLowerCase() === trimmed.toLowerCase();
              });

              if (duplicate) {
                setFilterNameError('A list with this name already exists');
                return;
              }
              // only clear duplicate error if it was set
              if (filterNameError === 'A list with this name already exists') {
                setFilterNameError('');
              }
            }
          }}
          variant={filterNameError && Input.Variant.Error}
          caption={filterNameError}
        />
      </div>

      <div className="create-edit-saved-list-modal--input-group">
        <p>Share with</p>
        <ProspectFiltersSelectDropdown
          className="w-100"
          options={shareWithOptions}
          optionRenderer={(option) => <span>{option.label}</span>}
          selectedOptionKey={sharedWith}
          selectedOptionRenderer={(option) => <span>{option.label}</span>}
          onChange={(option) => {
            setSharedWith(option.key);

            if (
              teamsList.length === 0 &&
              canViewTeamOption &&
              option.key === SharedWith.Team &&
              !getTeamsListRequestStatusLoading
            ) {
              sendGetTeamsListRequest();
            }
          }}
        />
      </div>

      {canViewTeamOption && sharedWith === SharedWith.Team && (
        <div className="create-edit-saved-list-modal--input-group">
          <p>Team</p>
          <ProspectFiltersMultiSelectDropdown
            className="teams-select-box w-100"
            selectClassName="teams-dropdown"
            btnClassName="w-100"
            placeholder="Select Teams"
            options={teamsOptions}
            optionRenderer={(option) => <span>{option?.name}</span>}
            selectedOptionKey={teamIds.map((id) => id.toString())}
            isLoading={getTeamsListRequestStatusLoading}
            showSearch
            onSearch={() => {}}
            filterOption={(search, option) =>
              option?.name?.toLowerCase().includes(search.toLowerCase())
            }
            selectedOptionRenderer={(selectedOptions) => {
              const maxChipsToShow = 2;

              const visibleChips = selectedOptions.slice(0, maxChipsToShow);
              const remainingChips = selectedOptions.slice(
                visibleChips.length - selectedOptions.length,
              );

              if (getTeamsListRequestStatusLoading) {
                return (
                  <span className="bs-select-box-text placeholder">
                    Select Teams
                  </span>
                );
              }

              return (
                <>
                  {visibleChips.map(
                    (option) =>
                      option?.name && (
                        <Pills
                          label={truncate(option?.name || '', 9, false)}
                          size="md"
                          showCloseIcon
                          onClose={() => {
                            setTeamIds(
                              selectedOptions
                                .filter((item) => item.key !== option.key)
                                .map((item) => Number(item.key)),
                            );
                          }}
                        />
                      ),
                  )}

                  {selectedOptions.length > 2 && remainingChips.length > 0 && (
                    <Pills
                      label={`+${remainingChips.length}`}
                      size="md"
                      showCloseIcon
                      onClose={() => {
                        const keysToRemove = remainingChips.map(
                          (item) => item.key,
                        );

                        setTeamIds(
                          selectedOptions
                            .filter((item) => !keysToRemove.includes(item.key))
                            .map((item) => Number(item.key)),
                        );
                      }}
                    />
                  )}
                </>
              );
            }}
            onChange={(options) => {
              setTeamIds(options.map((option) => option.id));
            }}
          />
        </div>
      )}
    </Modal>
  );
};

export default CreateEditSavedListModal;
